import { useState, useEffect, useRef } from "react";
import { toJS } from "mobx";
import { useNavigate } from "react-router-dom";
import CurriculumStore from "../stores/CurriculumStore";

export default function useCourse(courseId, { curriculumId } = {}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState();
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const loadedCourseIdRef = useRef(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!courseId) return; // Don't do anything if no courseId

    // Already loaded this exact course
    if (loadedCourseIdRef.current === courseId) return;

    // Currently fetching - don't start another fetch
    if (fetchingRef.current) return;

    const matchingCourse = CurriculumStore?.coursesById[courseId];
    if (matchingCourse?.id) {
      setCourse(toJS(matchingCourse));
      loadedCourseIdRef.current = courseId;
      setLoading(false);
    } else {
      // Need to fetch
      loadedCourseIdRef.current = null;
      fetchingRef.current = true;
      setLoading(true);
      setError(null);
      setNotFound(false);

      (async () => {
        try {
          // Initialization first so a cold load does not race the courses fetch.
          await CurriculumStore.ensureInitialized();
          const course = CurriculumStore?.coursesById[courseId];
          if (!course?.id) {
            setNotFound(true);
            loadedCourseIdRef.current = courseId;
            navigate(`/curriculum/${curriculumId}`, { replace: true });
          } else {
            setCourse(toJS(course));
            loadedCourseIdRef.current = courseId;
          }
        } catch (err) {
          // Fetch failure is not a missing course - do not redirect.
          console.error(err);
          setError(err);
        } finally {
          setLoading(false);
          fetchingRef.current = false;
        }
      })();
    }
  }, [courseId, curriculumId, reloadNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lets an error state retry the load without a full page refresh.
  const reload = () => {
    loadedCourseIdRef.current = null;
    fetchingRef.current = false;
    setError(null);
    setNotFound(false);
    setReloadNonce(n => n + 1);
  };

  return { course: course || {}, loading, error, notFound, reload };
}
